import { z } from 'zod'

import { getSessionContext } from '../session-context'
import { normalizeFilters } from './normalize-filters'
import type { AiChatBotTool, SessionContext } from '../types'

export interface AiChatBotToolConfig<T> {
  name: string
  description: string | (() => string)
  inputSchema: () => z.ZodType<T>
  concurrencySafe?: boolean
  cacheable?: boolean
  run: (args: T, ctx: SessionContext, toolCallId?: string) => Promise<unknown>
}

export function defineTool<T>(
  config: AiChatBotToolConfig<T>,
): AiChatBotTool<T> {
  let cachedSchema: z.ZodType<T> | null = null
  const getSchema = (): z.ZodType<T> => (cachedSchema ??= config.inputSchema())
  const resolveDescription = (): string =>
    typeof config.description === 'function'
      ? config.description()
      : config.description

  return {
    name: config.name,
    description: resolveDescription(),
    concurrencySafe: config.concurrencySafe ?? true,
    cacheable: config.cacheable ?? true,

    toOpenAI() {
      return {
        type: 'function' as const,
        function: {
          name: config.name,
          description: resolveDescription(),
          parameters: z.toJSONSchema(getSchema()) as Record<string, unknown>,
        },
      }
    },

    async run(rawArgs: T, toolCallId?: string): Promise<unknown> {
      // `as T`: Zod infers z.infer<Schema> but the tool interface erases to AiChatBotTool<unknown> at call sites — cast is safe because the schema was built for T.
      let parsed = getSchema().parse(rawArgs) as T
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'filters' in parsed &&
        parsed.filters !== null &&
        typeof parsed.filters === 'object'
      ) {
        parsed = {
          ...parsed,
          filters: normalizeFilters(parsed.filters as Record<string, unknown>),
        }
      }
      const ctx = await getSessionContext()
      return config.run(parsed, ctx, toolCallId)
    },
  }
}
